import { ConfigurationService } from '@sc/data-access-configuration';
import { InsightService } from '@sc/data-access-http-services';
import {
  Appointment,
  DotFitter,
  FetchFreeSlotsApi,
  Fitter,
  GeoCoordinates,
  Score,
  SearchAvance,
} from '@sc/data-access-models';
import {
  AppointmentsSandboxService,
  DossierSandboxService,
  DotFittersSandboxService,
  FittersSandboxService,
  SandboxService,
  ScoresSandboxService,
} from '@sc/data-access-state-management';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { AppointmentUtils, dayjs } from '@sc/utils';
import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscription,
  combineLatest,
  merge,
  of,
} from 'rxjs';
import { delay, filter, map, switchMap, take, tap } from 'rxjs/operators';
import {
  AgregatedAppointments,
  AppointmentSubParams,
  InputObservables,
  PrimaryFilters,
  SearchSlotsError,
  SearchStatus,
} from '../types';

export class AppointmentsSubService {
  subscriptionList: Subscription[] = [];

  //#region Data Sources
  rawAppointments$ = new BehaviorSubject<Appointment[]>([]);
  private appointments: Appointment[] = [];
  appointments$ = this.rawAppointments$.pipe(
    map((appointments) =>
      this.removeDuplicates(
        appointments,
        'fitterId',
        'id',
        'outsideIntervention'
      )
    ),
    map((appointments) =>
      appointments.slice().sort((appointmentA, appointmentB) => {
        return (
          new Date(appointmentA.time).getTime() -
          new Date(appointmentB.time).getTime()
        );
      })
    ),
    map((appointments) => this.filterMobility(appointments)),
    map((appointments) => this.filterReplacementVehicle(appointments)),
    switchMap((appointments) => this.filterSelectedAppointment(appointments)),
    map((appointments) => this.primaryFilters.appointmentsFilter(appointments)),
    tap((a) => (this.appointments = a))
  );

  rawFitters$ = new BehaviorSubject<Fitter[]>([]);
  private fitters: Fitter[] = [];
  fitters$ = this.rawFitters$.pipe(
    map((fitters) => this.removeDuplicates(fitters, 'brandId', 'id')),
    map((fitters) => this.primaryFilters.fittersFilter(fitters)),
    tap((f) => (this.fitters = f))
  );

  rawDotFitters$ = new BehaviorSubject<DotFitter[]>([]);
  private dotFitters: DotFitter[] = [];
  dotFitters$ = this.rawDotFitters$.pipe(
    map((dotFitters) => this.removeDuplicates(dotFitters, 'brandId', 'id')),
    map((dotFitters) => this.primaryFilters.dotFittersFilter(dotFitters)),
    tap((df) => (this.dotFitters = df))
  );
  public fittersIDWithOutsideApp$: Observable<string[]> =
    this.appointmentsSB.outsideAppointment$.pipe(
      map((appointmentList) => {
        const list = appointmentList.map((appointment) => appointment.fitterId);
        return Array.from(new Set(list));
      })
    );
  private fittersIDWithOutsideApp = [];
  public noDotFittersIDWithOutsideApp$: Observable<string[]> = combineLatest([
    this.dotFitters$,
    this.fittersIDWithOutsideApp$,
  ]).pipe(
    map(([dotFitters, fittersIDWithOutsideApp]) => {
      return fittersIDWithOutsideApp.filter(
        (id) => !dotFitters.some((f) => f.id === id && f.displayAppointments)
      );
    })
  );
  noAppDotFitters$ = new BehaviorSubject<DotFitter[]>([]);

  rawScores$ = new BehaviorSubject<Score[]>([]);
  scores$ = this.rawScores$.pipe(
    map((scores) => this.removeDuplicates(scores, 'fitterId')),
    map((scores) => this.primaryFilters.scoresFilter(scores))
  );
  //#endregion

  searchStatus$ = new BehaviorSubject<SearchStatus>('WAITING');

  terminalSearchStatus$ = this.searchStatus$.pipe(
    filter(
      (searchStatus) =>
        searchStatus === 'ERROR' ||
        searchStatus === 'NO_APPOINTMENTS' ||
        searchStatus === 'SUCCESS'
    )
  );

  fetchFreeSlotsError$ = new Subject<SearchSlotsError>();

  private readonly _nbDisplayedWithSlotFitters$ = new BehaviorSubject<number>(
    0
  );
  public nbDisplayedWithSlotFitters$ =
    this._nbDisplayedWithSlotFitters$.asObservable();

  private readonly _nbDisplayedNoSlotFitters$ = new BehaviorSubject<number>(10);
  public nbDisplayedNoSlotFitters$ =
    this._nbDisplayedNoSlotFitters$.asObservable();

  //#region Altered Observables

  scoredFitters$: Observable<Fitter[]> = combineLatest([
    this.fitters$,
    this.scores$,
  ]).pipe(
    // Put the scores in each fitter
    map(([usedFitters, usedScores]) => {
      return usedFitters.map(
        (fitter): Fitter => ({
          ...fitter,
          globalScore:
            usedScores.find((score) => score.fitterId === fitter.id)
              ?.globalScore ?? fitter.globalScore,
        })
      );
    })
  );

  //#region Sorted by Score Fitters
  /**
   * Gives the fitters of the store, sorted by score then by distance
   */
  sortedByScoreFitters$: Observable<Fitter[]> = this.scoredFitters$.pipe(
    // Sort the fitters according to their scores, then by their distance
    map((scoredList) =>
      scoredList.slice().sort((fitterA, fitterB) => {
        let scoreDiff = AppointmentsSubService.getDiff(
          fitterB.globalScore,
          fitterA.globalScore,
          0
        );

        if (scoreDiff === 0) {
          scoreDiff = AppointmentsSubService.getDiff(
            fitterA.brandOrder,
            fitterB.brandOrder
          );
        }

        if (scoreDiff === 0) {
          scoreDiff = AppointmentsSubService.getDiff(
            fitterA.distance ??
              this.dotFitters.find((df) => df.id === fitterA.id)?.distance,
            fitterB.distance ??
              this.dotFitters.find((df) => df.id === fitterB.id)?.distance
          );
        }

        return scoreDiff;
      })
    )
  );
  //#endregion

  //#region Sorted by Dist Fitters
  /**
   * Gives the fitters of the store, sorted by distance then by score
   */
  sortedByDistFitters$: Observable<Fitter[]> = this.scoredFitters$.pipe(
    // Sort the fitters according to their distance, then by their scores
    map((scoredList) =>
      scoredList.slice().sort((fitterA, fitterB) => {
        let distDiff = AppointmentsSubService.getDiff(
          fitterA.distance ??
            this.dotFitters.find((df) => df.id === fitterA.id)?.distance,
          fitterB.distance ??
            this.dotFitters.find((df) => df.id === fitterB.id)?.distance
        );

        if (distDiff === 0) {
          distDiff = AppointmentsSubService.getDiff(
            fitterB.globalScore,
            fitterA.globalScore,
            0
          );
        }

        if (distDiff === 0) {
          return AppointmentsSubService.getDiff(
            fitterA.brandOrder,
            fitterB.brandOrder
          );
        }
        return distDiff;
      })
    )
  );
  //#endregion

  sortedFitters$: Observable<Fitter[]> = this.inputObs.sortStrategy.pipe(
    switchMap((strategy) => {
      switch (strategy) {
        case 'dist-score':
          return this.sortedByDistFitters$;
        case 'score-dist':
        default:
          return this.sortedByScoreFitters$;
      }
    }),
    switchMap((fitters) =>
      combineLatest([
        of(fitters),
        this.dotFitters$,
        this.fittersIDWithOutsideApp$,
      ])
    ),
    map(([fitters, dotFitters, fittersIDWithOutsideApp]) => {
      const resultLists = {
        displayed: [],
        mobiles: [],
        cached: [],
      };

      const hasMobileAfterCenter = this.featureFlipService.isFeatureActivated(
        'mobile-service-after-center'
      );

      const hasMobileBeforeCenter = this.featureFlipService.isFeatureActivated(
        'mobile-service-before-center'
      );

      fitters.forEach((f) => {
        const isItinerant = this.fittersSB.isItinerant(f);
        const hasMobilitySlots =
          fittersIDWithOutsideApp &&
          fittersIDWithOutsideApp.find((fitterId) => f.id === fitterId)
            ?.length > 0;
        const shouldDisplaySlots = dotFitters.find(
          (dot) => dot.id === f.id
        )?.displayAppointments;

        if (hasMobileAfterCenter || hasMobileBeforeCenter) {
          if (isItinerant) {
            resultLists.mobiles.push(f);
          } else if (shouldDisplaySlots) {
            resultLists.displayed.push(f);
          } else if (hasMobilitySlots) {
            resultLists.mobiles.push(f);
          } else {
            resultLists.cached.push(f);
          }
        } else {
          if (shouldDisplaySlots || isItinerant || hasMobilitySlots) {
            resultLists.displayed.push(f);
          } else resultLists.cached.push(f);
        }
      });

      if (resultLists.mobiles?.length > 0) {
        resultLists.mobiles.sort((fitterA, fitterB) => {
          let scoreDiff = AppointmentsSubService.getDiff(
            fitterB.globalScore,
            fitterA.globalScore,
            0
          );

          if (scoreDiff === 0) {
            scoreDiff = AppointmentsSubService.getDiff(
              fitterA.brandOrder,
              fitterB.brandOrder
            );
          }

          return scoreDiff;
        });
      } else {
        resultLists.displayed.sort((fitterA, fitterB) => {
          let scoreDiff = AppointmentsSubService.getDiff(
            fitterA.brandOrder,
            fitterB.brandOrder
          );

          if (scoreDiff === 0) {
            scoreDiff = AppointmentsSubService.getDiff(
              fitterB.globalScore,
              fitterA.globalScore,
              0
            );
          }

          return scoreDiff;
        });
      }

      if (hasMobileAfterCenter) {
        return [
          ...resultLists.displayed,
          ...resultLists.mobiles,
          ...resultLists.cached,
        ];
      } else if (hasMobileBeforeCenter) {
        return [
          ...resultLists.mobiles,
          ...resultLists.displayed,
          ...resultLists.cached,
        ];
      } else {
        return [...resultLists.displayed, ...resultLists.cached];
      }
    })
  );

  //#region Displayed Fitters
  displayedFitters$: Observable<Fitter[]> = combineLatest([
    this.sortedFitters$,
    this.dotFitters$,
    this.appointments$,
    this.inputObs.generalists,
  ]).pipe(
    // Keep only the fitters which have one of these conditions, in order to display them :
    // - a corresponding dotFitter with displayAppointment
    // - at least one appointment that is an outside intervention
    // - a fitter type that is MOBILE only
    // These conditions are tested after filtering out the generalist fitters, if needed
    map(
      ([usedFitters, usedDotFitters, usedAppointments, displayGeneralists]) => {
        return usedFitters.filter(
          (fitter) =>
            (!!displayGeneralists ||
              !this.featureFlipService.isBrandFeatureActivated(
                fitter.brandId,
                'fitter-generalist-service'
              )) &&
            (this.fittersSB.isItinerant(fitter) ||
              usedAppointments.some(
                (apmt) =>
                  apmt.fitterId === fitter.id && apmt.outsideIntervention
              ) ||
              !!usedDotFitters.find((df) => df.id === fitter.id)
                ?.displayAppointments)
        );
      }
    )
  );
  //#endregion

  //#region Displayed Fitters that are in a weather event
  weatherEventFitterCards$: Observable<Fitter[]> = combineLatest([
    this.displayedFitters$,
    this.fittersSB.weatherEvents$,
  ]).pipe(
    map(([displayedFitters, weatherEvents]) =>
      displayedFitters.filter((fitter) =>
        weatherEvents.some((weatherEvent) =>
          weatherEvent.fitters?.[fitter?.brandId]?.includes(fitter.id)
        )
      )
    )
  );
  //#endregion

  //#region Displayed Fitters With search version NO_SLOT
  noAppointmentFitterCards$: Observable<Fitter[]> = combineLatest([
    this.displayedFitters$,
    this.inputObs?.specialist,
  ]).pipe(
    map(([displayedFitters, specialist]) => {
      return displayedFitters.filter((f) => {
        const isNoSlot =
          this.configService.getBrandSearchVersion(f.brandId) === 'NO_SLOT';

        const hasGeneralists =
          this.featureFlipService.brandFeatureActivatedExists(
            'fitter-generalist-service'
          );

        return isNoSlot && (!hasGeneralists || !specialist);
      });
    })
  );
  //#endregion

  //#region Displayed Fitters With search version NO_SLOT
  displayedNoAppointmentFitterCards$: Observable<Fitter[]> = combineLatest([
    this.noAppointmentFitterCards$,
    this.weatherEventFitterCards$,
    this._nbDisplayedNoSlotFitters$,
  ]).pipe(
    map(([displayedFitterCards, weatherEventFitters, nbDisplayed]) =>
      displayedFitterCards
        .concat(...weatherEventFitters)
        .filter((_, i) => i < nbDisplayed)
    )
  );
  //#endregion

  //#region Displayed Fitters With No Appointment
  displayedFittersWithNoAppointment$: Observable<Fitter[]> = combineLatest([
    this.appointments$,
    this.displayedFitters$,
    this.noAppointmentFitterCards$,
  ]).pipe(
    // Keep only the fitters which have no appointment associated but could have (do not include NO_SLOT fitters)
    map(([usedAppointments, usedFitters, noSlotFitterIds]) => {
      return usedFitters.filter(
        (fitter) =>
          this.sandboxService.canDoRequestAppointment(fitter.brandId) &&
          !usedAppointments.some((apmt) => apmt.fitterId === fitter.id) &&
          !noSlotFitterIds.some((f) => f.id === fitter.id)
      );
    })
  );
  //#endregion

  //#region Displayed Appointments
  private displayedAppointments: Appointment[] = [];
  displayedAppointments$: Observable<Appointment[]> = combineLatest([
    this.appointments$,
    this.displayedFitters$,
    this.dotFitters$,
    this.weatherEventFitterCards$,
    this.inputObs.specialist,
  ]).pipe(
    // For each displayed fitter, select each appointment of this fitter
    map(
      ([
        usedAppointments,
        usedFitters,
        usedDotFitters,
        fittersInWeatherEvent,
        displaySpecialists,
      ]) => {
        return usedAppointments
          .filter((appointment) => {
            // The appointments selected must have the same restrictions as stated in displaydeFitter$ and correspond to a displayed fitter
            const foundFitter = usedFitters.find(
              (fitter) => fitter.id === appointment.fitterId
            );

            return (
              displaySpecialists &&
              !!foundFitter &&
              !fittersInWeatherEvent.some(
                (fitterInWeatherEvent) =>
                  fitterInWeatherEvent.id === appointment.fitterId
              ) &&
              (usedDotFitters.find((dotF) => dotF.id === appointment.fitterId)
                ?.displayAppointments ||
                appointment.outsideIntervention ||
                this.fittersSB.isItinerant(foundFitter))
            );
          })
          .sort((a1, a2) => {
            const indexFitter1 = usedFitters.findIndex(
              (f) => f.id === a1.fitterId
            );
            const indexFitter2 = usedFitters.findIndex(
              (f) => f.id === a2.fitterId
            );

            return indexFitter1 - indexFitter2;
          });
      }
    ),

    tap((appointments) => (this.displayedAppointments = appointments))
  );
  //#endregion

  //#region First Appointment
  firstAppointment$: Observable<Appointment | null> =
    this.displayedFitters$.pipe(
      map((displayedFitters) => displayedFitters[0]?.id),
      switchMap((fitterId) =>
        this.displayedAppointments$.pipe(
          map(
            (displayedApps) =>
              displayedApps.find((a) => a.fitterId === fitterId) ?? null
          )
        )
      )
    );
  //#endregion First Appointment

  //#region Appointments mapped by fitters
  appointmentsByFitter$: Observable<Map<string, Appointment[]>> =
    this.displayedAppointments$.pipe(
      map((appointments) => AppointmentUtils._groupByFitters(appointments))
    );
  //#endregion

  private static readonly newSearchAvance: SearchAvance = {
    nbCache: 0,
    requestCacheList: [],
    nbDisplay: 0,
    requestDisplayList: [],
    nbTot: 0,
    receiveList: [],
  };

  private searchAvance: SearchAvance = AppointmentsSubService.newSearchAvance;
  searchAvance$ = merge(
    this.dotFitters$.pipe(
      map((dotFitters) => {
        return {
          nbCache: dotFitters.filter((df) => df.cacheAppointments).length,
          requestCacheList: dotFitters
            .filter((df) => df.cacheAppointments)
            .map((f) => f.id),
          nbDisplay: dotFitters.filter((df) => df.displayAppointments).length,
          requestDisplayList: dotFitters
            .filter((df) => df.displayAppointments)
            .map((f) => f.id),
          nbTot: dotFitters.length,
          receiveList: [],
        };
      }),
      tap((avance) => (this.searchAvance = avance))
    ),
    this.rawAppointments$.pipe(
      map((rawAppointments) => {
        if (this.searchAvance) {
          this.searchAvance.receiveList = [
            ...new Set(rawAppointments.map((app) => app.fitterId)),
          ];
        }
        return this.searchAvance;
      }),
      tap((avance) => (this.searchAvance = avance))
    )
  );

  //#region Appointments by Fitter by Day
  /**
   * Map of appointments that divide them first by fitter (the outermost string), then by day (the innermost string)
   * appointmentsByFitterByDay.get(fitterId).get(day)
   */
  appointmentsByFitterByDay$: Observable<AgregatedAppointments> =
    this.appointmentsByFitter$.pipe(
      map((appointmentsByFitterMap) => {
        const resultMap = new Map<string, Map<string, Appointment[]>>();

        appointmentsByFitterMap.forEach((appointments, fitterId) => {
          const innerMap = AppointmentUtils._groupByDays(appointments);

          resultMap.set(fitterId, innerMap);
        });

        return resultMap;
      })
    );
  //#endregion

  //#region Filtered Appointments Map By Fitter By Day
  /**
   * The Map of appointments grouped by fitter and then by day, but with user input filters applied
   */
  filteredAppointmentsMapByFitterByDay$: Observable<AgregatedAppointments> =
    this.applyAppointmentsMapFilters(this.appointmentsByFitterByDay$);
  //#endregion

  //#region Appointments mapped by Day
  appointmentsByDay$: Observable<Map<string, Appointment[]>> =
    this.displayedAppointments$.pipe(
      map((appointments) => AppointmentUtils._groupByDays(appointments))
    );
  //#endregion

  //#region Appointments by Day by Fitter
  /**
   * Map of appointments that divide them first by day (the outermost string), then by fitter (the innermost string)
   * appointmentsByDayByFitter.get(day).get(fitterId)
   */
  appointmentsByDayByFitter$: Observable<AgregatedAppointments> =
    this.appointmentsByDay$.pipe(
      map((appointmentsByDayMap) => {
        const resultMap = new Map<string, Map<string, Appointment[]>>();

        appointmentsByDayMap.forEach((appointments, fitterId) => {
          const innerMap = AppointmentUtils._groupByFitters(appointments);

          resultMap.set(fitterId, innerMap);
        });

        return resultMap;
      })
    );
  //#endregion

  //#region Filtered Appointments Map By Fitter By Day
  /**
   * The Map of appointments grouped by day and then by fitter, but with user input filters applied
   */
  filteredAppointmentsMapByDayByFitter$: Observable<AgregatedAppointments> =
    this.applyAppointmentsMapFilters(this.appointmentsByDayByFitter$);
  choseAppointmentsMap$: Observable<AgregatedAppointments> =
    this.inputObs.appointmentMap.pipe(
      switchMap((choice) => {
        switch (choice) {
          case 'days':
            return this.filteredAppointmentsMapByDayByFitter$;
          case 'fitters':
          default:
            return this.filteredAppointmentsMapByFitterByDay$;
        }
      })
    );
  //#endregion

  //#region Fitters In Cache
  fittersInCache$: Observable<string[]> = this.sortedFitters$.pipe(
    map((fitters) =>
      fitters
        .filter(
          (f) => this.appointments.findIndex((a) => f.id === a.fitterId) > -1
        )
        .map((f) => f.id)
    )
  );
  //#endregion Fitters In Cache

  //#region Calendar Appointments
  calendarAppointments$: Observable<Map<string, number>> =
    this.appointmentsByDay$.pipe(
      map((appointmentsByDayMap) => {
        const resultMap = new Map<string, number>();

        appointmentsByDayMap.forEach((appointmentsList, day) => {
          resultMap.set(day, appointmentsList.length);
        });

        return resultMap;
      })
    );
  //#endregion

  //#region Appointments shown on the Map
  /**
   * Gives the appointments that are meant to be shown as bubbles on the map
   */
  firstProposedAppointments$: Observable<Appointment[]> =
    this.filteredAppointmentsMapByFitterByDay$.pipe(
      map((appointmentsOuterMap) => {
        const resultAppointments: Appointment[] = [];

        appointmentsOuterMap.forEach((appointmentsInnerMap) => {
          const innerAppointmentsList: Appointment[] = [];

          appointmentsInnerMap.forEach((appointments) =>
            innerAppointmentsList.push(...appointments)
          );

          const sortedAppointment = innerAppointmentsList
            .filter((appointment) => !appointment.outsideIntervention)
            .sort(
              (appointment1, appointment2) =>
                new Date(appointment1.time).getTime() -
                new Date(appointment2.time).getTime()
            )
            .find((_, i) => i === 0);

          resultAppointments.push(sortedAppointment);
        });

        return resultAppointments;
      })
    );

  firstProposedOutsideIntervention$: Observable<Appointment | null> =
    this.filteredAppointmentsMapByDayByFitter$.pipe(
      map((appointmentsOuterMap) => {
        let resultAppointment: Appointment = null;

        appointmentsOuterMap.forEach((appointmentsInnerMap) => {
          const innerAppointmentsList: Appointment[] = [];

          appointmentsInnerMap.forEach((appointments) =>
            innerAppointmentsList.push(...appointments)
          );

          const sortedAppointment = innerAppointmentsList
            .filter((appointment) => appointment.outsideIntervention)
            .sort(
              (appointment1, appointment2) =>
                new Date(appointment1.time).getTime() -
                new Date(appointment2.time).getTime()
            )
            .find((_, i) => i === 0);

          if (
            sortedAppointment &&
            (!resultAppointment ||
              new Date(sortedAppointment.time).getTime() <
                new Date(resultAppointment.time).getTime())
          ) {
            resultAppointment = sortedAppointment;
          }
        });

        return resultAppointment;
      })
    );
  //#endregion

  //#endregion

  private canTimeout = false;

  private currentMobilePos: GeoCoordinates = null;
  private readonly cachedMobileAppointments: {
    coordinates: GeoCoordinates;
    apmtIds: string[];
  }[] = [];

  constructor(
    private readonly sandboxService: SandboxService,
    public appointmentsSB: AppointmentsSandboxService,
    public fittersSB: FittersSandboxService,
    public dotFittersSB: DotFittersSandboxService,
    public scoresSB: ScoresSandboxService,
    public dossierSB: DossierSandboxService,
    private readonly featureFlipService: FeatureFlippingManagerService,
    private readonly configService: ConfigurationService,
    private readonly logger: InsightService,
    private readonly primaryFilters: PrimaryFilters,
    private readonly inputObs: InputObservables,
    private readonly parameters: AppointmentSubParams
  ) {
    this.fittersIDWithOutsideApp$.subscribe((list) => {
      this.fittersIDWithOutsideApp = list;
    });
  }

  get inputObservables() {
    return { ...this.inputObs };
  }

  /**
   * Function that applies a generic filter to each groupment of appointments inside an agregated map
   * @param mapAppointments The map of appointments to filter
   * @param func The function that filters the appointments in each group
   * @returns The filtered map of appointments
   */
  private static filterAppointmentsMap(
    mapAppointments: AgregatedAppointments,
    func: (input: Appointment[]) => Appointment[]
  ): AgregatedAppointments {
    const resultMap = new Map<string, Map<string, Appointment[]>>();

    mapAppointments.forEach((appointmentsSubMap, firstKey) => {
      const resultSubMap = new Map<string, Appointment[]>();

      appointmentsSubMap.forEach((appointmentsList, secondKey) => {
        // Apply the filter on the list of each group and reconstitute the map
        const filteredAppointmentsList = func(appointmentsList);

        if (filteredAppointmentsList.length > 0) {
          resultSubMap.set(secondKey, filteredAppointmentsList);
        }
      });

      if (resultSubMap.size > 0) {
        resultMap.set(firstKey, resultSubMap);
      }
    });

    return resultMap;
  }

  /**
   * Function that applies a generic filter to an appointment agregation map
   * @param mapObs The observable to change
   * @param inputObs The user input observable
   * @param filteringBool When the user input indicates that the filter should be applied
   * @param func The filtering function
   * @returns The observable containing the filters
   */
  private static addFilter(
    mapObs: Observable<AgregatedAppointments>,
    inputObs: Observable<boolean>,
    filteringBool: boolean,
    func: (input: Appointment[]) => Appointment[]
  ): Observable<AgregatedAppointments> {
    return inputObs.pipe(
      switchMap((inputBool) =>
        inputBool === filteringBool
          ? mapObs.pipe(
              map((appointmentsMap) => {
                return AppointmentsSubService.filterAppointmentsMap(
                  appointmentsMap,
                  func
                );
              })
            )
          : mapObs
      )
    );
  }

  private static getDiff(
    value1: number,
    value2: number,
    defaultValue = Infinity
  ) {
    const diff = (value1 ?? defaultValue) - (value2 ?? defaultValue);

    if (isNaN(diff)) {
      return 0;
    }

    return diff;
  }
  //#endregion

  initSubscriptions() {
    this.unsubscribeAll();

    this.startFitterCounts();

    this.subscribeToStatus();
    this.subscribeToFreeSlots();
    this.subscribeToFitters();
    this.subscribeToDotFitters();
    this.subscribeToAppointments();
    this.subscribeToScores();
  }

  destroy() {
    this.unsubscribeAll();
  }

  clearAll() {
    this.appointmentsSB.clearAppointments();
    this.fittersSB.clearFitters();
    this.dotFittersSB.clearDotFitters();
    this.scoresSB.clearScores();
  }

  clearMobileFitters() {
    this.fittersSB.removeFitters(
      this.fitters.filter((f) => f.fitterType === 'MOBILE').map((f) => f.id)
    );

    this.appointmentsSB.removeAppointments(
      this.appointments.filter((a) => a.outsideIntervention).map((a) => a.id)
    );
  }

  public clearSearchAvance() {
    this.searchAvance.nbDisplay = -1;
  }

  public displayMoreWithSlotFitters(added = 2) {
    combineLatest([
      this._nbDisplayedWithSlotFitters$,
      this.appointmentsByFitterByDay$,
      this.displayedFittersWithNoAppointment$,
    ])
      .pipe(take(1))
      .subscribe(([nbFitters, apmtByFitterByDay, noApmtFitters]) => {
        const oldNumbDisplay = nbFitters;

        const numbFittersMax =
          Array.from(apmtByFitterByDay?.keys() || []).length +
          (noApmtFitters?.length ?? 0);
        const numbHiddenFitters = numbFittersMax - oldNumbDisplay;

        const newNumFittersDisplayed =
          numbHiddenFitters - added > 0
            ? oldNumbDisplay + added
            : numbFittersMax;

        this._nbDisplayedWithSlotFitters$.next(newNumFittersDisplayed);

        if (numbHiddenFitters < added) {
          this.dotFittersSB.showMoreDotFitters();
        }
      });
  }

  public displayMoreNoSlotFitters(added = 10) {
    this._nbDisplayedNoSlotFitters$.next(
      this._nbDisplayedNoSlotFitters$.getValue() + added
    );
  }

  /**
   * Function used in order to begin an appointment search on the backend, using the current dossier claim
   * @param fetchParams The parameters to use on the appointment search
   */
  async fetchFreeSlots(
    fetchParams: Omit<FetchFreeSlotsApi, 'clientKey'>,
    allowRestartOnTimeout = true
  ) {
    // Always wait for the GQL subscription to be ready
    await this.appointmentsSB.waitReadyness();

    this.dossierSB.dossier$
      .pipe(
        filter((dossier) => !!dossier),
        take(1),
        switchMap((dossier) => {
          const fetchFreeSlotsBody: FetchFreeSlotsApi = {
            ...fetchParams,
            claim: dossier.claim,
            clientKey: this.appointmentsSB.getClientKey().toString(),
            businessOrigin: dossier.creationData.businessOrigin,
          };

          // Rememeber the body and set state to searching
          this.searchStatus$.next('SEARCHING');
          this.canTimeout = true;

          // Remember the geocoordinates of the search (not from the map) in order to know which mobile appointments to show
          this.currentMobilePos =
            fetchParams.searchLocation?.address?.geoCoordinates || null;

          this.startFetchTimeout(
            fetchFreeSlotsBody,
            dossier?.id,
            !!allowRestartOnTimeout
          );

          // Start the orchestration
          return this.appointmentsSB.fetchFreeSlots(fetchFreeSlotsBody);
        })
      )
      .subscribe();
  }

  private startFetchTimeout(
    fetchParams: FetchFreeSlotsApi,
    dossierId: string,
    allowRestartOnTimeout = true
  ) {
    // callback to check if the search is sucessfull
    setTimeout(async () => {
      if (
        this.searchStatus$.value === 'WAITING' ||
        this.searchStatus$.value === 'SEARCHING'
      ) {
        this.searchStatus$.next('SUCCESS');
      }
      if (this.dotFitters.length == 0 || this.appointments.length == 0) {
        // no dotFitter -> check the subscrition
        const subscriptionOk = await this.sandboxService.checkSubscription(); // check the subscription and relaunch it if need
        if (!subscriptionOk) {
          // the subscription was ko after 10s -> wait for readyness and restart the search
          const isReady = await this.appointmentsSB.waitReadyness();
          if (isReady) {
            console.log('Relaunch Search');
            this.fetchFreeSlots(fetchParams);
          } else {
            // the subscription was KO a second time after 10s
            this.logger.trackException({
              exception: new Error('Subscription KO'),
            });
          }
        } else {
          console.error('Subscription OK but no result after 15s');
          this.logger.trackException({
            exception: new Error('Subscription OK but no result after 15s'),
            properties: {
              clientKey: this.appointmentsSB.getClientKey().toString(),
              fetchParams,
              dossierId,
              restarted: allowRestartOnTimeout,
            },
          });
          this.searchStatus$.next('ERROR');
          if (allowRestartOnTimeout) {
            this.fetchFreeSlots(fetchParams, false);
          }
        }
      }
    }, 15000);
  }

  private applyAppointmentsMapFilters(
    appointmentsMapObs: Observable<AgregatedAppointments>
  ): Observable<AgregatedAppointments> {
    return appointmentsMapObs.pipe(
      // The filter on the "In Center" input
      switchMap((mappedAppointments) =>
        AppointmentsSubService.addFilter(
          of(mappedAppointments),
          this.inputObs.inCenter,
          false,
          (appointmentsList) =>
            appointmentsList.filter(
              (appointment) => appointment.outsideIntervention
            )
        )
      ),

      // The filter on the "At Home" input
      switchMap((mappedAppointments) =>
        AppointmentsSubService.addFilter(
          of(mappedAppointments),
          this.inputObs.atHome,
          false,
          (appointmentsList) =>
            appointmentsList.filter(
              (appointment) => !appointment.outsideIntervention
            )
        )
      ),

      // The filter on the "Replacement Vehicle" input
      switchMap((mappedAppointments) =>
        AppointmentsSubService.addFilter(
          of(mappedAppointments),
          this.inputObs.replacementVehicle,
          true,
          (appointmentsList) =>
            appointmentsList.filter(
              (appointment) =>
                !appointment.outsideIntervention &&
                appointment.replacementVehicle
            )
        )
      ),

      // The filter on the "Period" input
      switchMap((mappedAppointments) =>
        this.inputObs.timeFilter.pipe(
          map((timeFilter) => {
            return timeFilter
              ? AppointmentsSubService.filterAppointmentsMap(
                  mappedAppointments,
                  (input) =>
                    input.filter((a) => {
                      const date = new Date(a.time);

                      const startDate = new Date(a.time);
                      startDate.setHours(timeFilter.start, 0, 0, 0);
                      const endDate = new Date(a.time);
                      endDate.setHours(timeFilter.end, 0, 0, 0);

                      return date >= startDate && date <= endDate;
                    })
                )
              : mappedAppointments;
          })
        )
      )
    );
  }

  private keepOneFitterInMobility(appointments: Appointment[]): Appointment[] {
    let usedAppointments = appointments;

    if (this.currentMobilePos) {
      const foundCached = this.cachedMobileAppointments.find((cachedItem) =>
        this.compareGeoCoords(cachedItem.coordinates, this.currentMobilePos)
      );

      const usedMobileApmtIds = foundCached?.apmtIds || [];

      usedAppointments = appointments.filter(
        (apmt) =>
          !apmt.outsideIntervention || usedMobileApmtIds.includes(apmt.id)
      );
    }

    // Only display the appointments of one fitter in mobility : the last fitter for which we recieved appointments (last in the list)
    const keptFitterId = [...usedAppointments]
      .reverse()
      .find((apmt) => apmt.outsideIntervention)?.fitterId;
    const keptAppointments = usedAppointments.filter(
      (apmt) => !apmt.outsideIntervention || apmt.fitterId === keptFitterId
    );

    return keptAppointments;
  }

  private filterMobility(appointments: Appointment[]): Appointment[] {
    if (
      this.parameters.checkMobility &&
      this.featureFlipService.isFeatureActivated(
        'insurer-can-declare-in-mobility'
      )
    ) {
      // Apply the in mobility filter if the feature flip isn't present and activated in service params
      return appointments.filter(
        (a) =>
          !a.outsideIntervention ||
          this.featureFlipService.isBrandFeatureActivated(
            a.brandId,
            'fitter-in-mobility-service'
          )
      );
    } else {
      return appointments;
    }
  }

  private filterReplacementVehicle(appointments: Appointment[]): Appointment[] {
    if (
      this.parameters.checkReplacementVehicle &&
      this.featureFlipService.isFeatureActivated(
        'can-declare-replacement-vehicle-insurer'
      )
    ) {
      // Apply the replacement vehicle filter if the feature flip isn't present and activated in service params
      return appointments.filter(
        (a) =>
          !a.replacementVehicle ||
          this.featureFlipService.isBrandFeatureActivated(
            a.brandId,
            'can-declare-replacement-vehicle-fitter'
          )
      );
    } else {
      return appointments;
    }
  }

  private filterSelectedAppointment(
    appointments: Appointment[]
  ): Observable<Appointment[]> {
    return this.dossierSB.dossier$.pipe(
      take(1),
      map((dossier) => {
        if (dossier?.appointment?.id) {
          return appointments.filter(
            (apmt) =>
              apmt.id !== dossier.appointment.id &&
              (new Date(dossier.appointment.time).toISOString() !==
                new Date(apmt.time).toISOString() ||
                dossier.appointment.fitterId !== apmt.fitterId ||
                dossier.appointment.outsideIntervention !==
                  apmt.outsideIntervention)
          );
        }

        return appointments;
      })
    );
  }

  private compareGeoCoords(
    geo1: GeoCoordinates,
    geo2: GeoCoordinates,
    precision = 5
  ): boolean {
    return (
      +geo1.lat.toFixed(precision) === +geo2.lat.toFixed(precision) &&
      +geo1.lon.toFixed(precision) === +geo2.lon.toFixed(precision)
    );
  }

  private cacheMobileAppointments(newAppointments: Appointment[]) {
    if (!this.currentMobilePos) {
      return;
    }

    const mobileApmtIdList = newAppointments
      .filter((apmt) => apmt.outsideIntervention)
      .map((apmt) => apmt.id);

    const foundCached = this.cachedMobileAppointments.find((cachedItem) =>
      this.compareGeoCoords(cachedItem.coordinates, this.currentMobilePos)
    );

    if (foundCached) {
      const excedent =
        foundCached.apmtIds.length +
        mobileApmtIdList.length -
        this.parameters.maxCachedMobileApmtByCoord;
      if (excedent > 0) {
        foundCached.apmtIds = foundCached.apmtIds.slice(excedent);
      }
      foundCached.apmtIds.push(...mobileApmtIdList);
    } else {
      this.cachedMobileAppointments.push({
        coordinates: this.currentMobilePos,
        apmtIds: mobileApmtIdList,
      });

      if (
        this.cachedMobileAppointments.length >
        this.parameters.maxCachedMobileCoords
      ) {
        this.cachedMobileAppointments.shift();
      }
    }
  }

  private removeDuplicates<T>(list: T[], ...idKeyList: (keyof T)[]): T[] {
    if (this.parameters.removeDuplicates) {
      const uniqueKeys = [];
      const uniqueValues: T[] = [];
      let key = 0;
      for (const item of list) {
        key = this.hash(`${idKeyList.map((idKey) => item[idKey]).join('')}`);
        if (!uniqueKeys.includes(key)) {
          uniqueKeys.push(key);
          uniqueValues.push(item);
        }
      }
      return uniqueValues;
    } else {
      return list;
    }
  }

  private hash(str: string): number {
    let hash = 0,
      i,
      chr;
    if (str.length === 0) return hash;
    for (i = 0; i < str.length; i++) {
      chr = str.charCodeAt(i);
      hash = (hash << 5) - hash + chr;
      hash |= 0; // Convert to 32bit integer
    }
    return hash;
  }

  private startFitterCounts() {
    const sub1 = combineLatest([
      this.dotFitters$,
      this.displayedFittersWithNoAppointment$,
      this.noAppointmentFitterCards$,
      this.noDotFittersIDWithOutsideApp$,
    ])
      .pipe(
        map(
          ([
            dotFitters,
            noApmtFitters,
            noSlotFitters,
            fittersWithOutsideApmt,
          ]) => {
            // dotFitters + 100%Mobile + fittersWithOutside (notdotFitters nor 100% mobile)
            return (
              (dotFitters?.filter(
                (df) =>
                  df.displayAppointments &&
                  !noSlotFitters.some((f) => f.id === df.id)
              )?.length ?? 0) +
              (noApmtFitters?.length ?? 0) +
              fittersWithOutsideApmt.length
            );
          }
        )
      )
      .subscribe((newFitterCount) => {
        this._nbDisplayedWithSlotFitters$.next(newFitterCount);
      });

    this.subscriptionList.push(sub1);
  }

  private subscribeToStatus() {
    const sub1 = this.searchStatus$
      .pipe(
        filter((newStatus) => newStatus === 'SEARCHING'),
        tap(() => (this.canTimeout = true)),
        delay(this.parameters.fetchSlotsTimeout)
      )
      .subscribe(() => {
        // After begining the search, if there is a timeout possible, show an error snackbar
        if (this.canTimeout) {
          this.fetchFreeSlotsError$.next('TIMEOUT');
          this.searchStatus$.next('ERROR');
          this.canTimeout = false;
        }
      });

    const sub2 = this.terminalSearchStatus$.subscribe((newStatus) => {
      // Intercept all new status that indicates the end of a search and disable timeouts for this search
      this.canTimeout = false;

      if (newStatus !== 'SUCCESS') {
        // If there is an error then show the right error
        if (newStatus === 'NO_APPOINTMENTS') {
          this.fetchFreeSlotsError$.next('NO_APPOINTMENT');
        } else if (newStatus === 'ERROR') {
          this.fetchFreeSlotsError$.next('ERROR');
        }
      }
    });

    this.subscriptionList.push(sub1);
    this.subscriptionList.push(sub2);

    this.searchStatus$.subscribe((s) => {
      // change dotFitter.noAppointments if no results
      if (s != 'SEARCHING' && s != 'WAITING') {
        this.noAppDotFitters$.next(
          this.dotFitters.filter(
            (df) =>
              this.searchAvance.receiveList.indexOf(df.id) == -1 &&
              df.displayAppointments &&
              this.configService.getBrandSearchVersion(df.brandId) !== 'NO_SLOT'
          )
        );
      } else {
        this.noAppDotFitters$.next([]);
      }
    });
  }

  private subscribeToFreeSlots(): void {
    const sub = this.appointmentsSB
      .subscribeToFreeSlotsResults()
      .subscribe((freeSlotsResults) => {
        // Update the status if recieved
        const receivedStatus = freeSlotsResults?.status as SearchStatus;
        if (receivedStatus) {
          let newStatus = receivedStatus;
          if (
            receivedStatus === 'SUCCESS' &&
            this.displayedAppointments.length === 0
          ) {
            newStatus = 'NO_APPOINTMENTS';
          }
          this.searchStatus$.next(newStatus);
        }

        // Update the scores if recieved
        if (freeSlotsResults?.globalScores?.length) {
          this.scoresSB.updateScores(freeSlotsResults.globalScores as Score[]);
        }

        // Update the appointments and fitters if recieved
        if (freeSlotsResults?.fitter) {
          if (freeSlotsResults?.fitter.length >= 0) {
            this.fittersSB.addFitters(freeSlotsResults?.fitter);
          } else this.fittersSB.addFitters([freeSlotsResults.fitter as Fitter]);
        }

        if (freeSlotsResults?.appointments) {
          this.canTimeout = false;
          const appointmentsToAdd = freeSlotsResults.appointments.map(
            (appointment: Appointment) => {
              return {
                ...appointment,
                time: dayjs(appointment.time)
                  .tz(appointment?.timezone || 'Europe/Paris')
                  .format('YYYY-MM-DDTHH:mm:ssZ'),
              };
            }
          );
          this.cacheMobileAppointments(appointmentsToAdd);
          this.appointmentsSB.addAppointments(appointmentsToAdd);
        }

        // Update the dotFitters if received
        if (freeSlotsResults?.dotFitters?.length) {
          this.dotFittersSB.addDotFitters(freeSlotsResults.dotFitters);
        }
      });

    this.subscriptionList.push(sub);
  }

  private subscribeToAppointments(): void {
    const sub = this.appointmentsSB.appointments$.subscribe((appointments) => {
      if (appointments) {
        this.rawAppointments$.next(appointments);
      }
    });

    this.subscriptionList.push(sub);
  }

  private subscribeToFitters(): void {
    const sub = this.fittersSB.fitters$.subscribe((fitters) => {
      if (fitters) {
        this.rawFitters$.next(fitters);
      }
    });

    this.subscriptionList.push(sub);
  }

  private subscribeToDotFitters(): void {
    const sub = this.dotFittersSB.dotFitters$.subscribe((dotFitters) => {
      if (dotFitters) {
        // Avoid blinking
        const oldNb = this.dotFitters.length;
        dotFitters.map((f) => {
          this.dotFitters.push(f);
        });
        this.dotFitters.splice(0, oldNb);
        this.rawDotFitters$.next(this.dotFitters);

        // Clear the appointments in the store that do not correspond to a known dotFitter
        this.appointmentsSB.removeAppointments(
          this.rawAppointments$
            .getValue()
            .filter(
              (a) =>
                !a.outsideIntervention &&
                dotFitters.findIndex((df) => df.id === a.fitterId) === -1
            )
            .map((app) => app.id)
        );

        // Clear the fitters in the store that do not correspond to a known dotFitter or to a MOBILE fitter
        this.fittersSB.removeFitters(
          this.rawFitters$
            .getValue()
            .filter(
              (f) =>
                dotFitters.findIndex((df) => df.id === f.id) === -1 &&
                !this.fittersSB.isItinerant(f) &&
                this.fittersIDWithOutsideApp.findIndex(
                  (fid) => fid === f.id
                ) === -1
            )
            .map((f) => f.id)
        );

        // Clear the scores in the store that do not correspond to a known dotFitter
        this.scoresSB.updateScores(
          this.rawScores$
            .getValue()
            .filter(
              (s) => dotFitters.findIndex((df) => df.id === s.fitterId) > -1
            )
        );
      }
    });

    this.subscriptionList.push(sub);
  }

  private subscribeToScores(): void {
    const sub = this.scoresSB.scores$.subscribe((scores) => {
      if (scores) {
        this.rawScores$.next(scores);
      }
    });

    this.subscriptionList.push(sub);
  }

  private unsubscribeAll() {
    this.subscriptionList.forEach((sub) => sub.unsubscribe());
    this.subscriptionList = [];
  }
}
